import type { MRT_ColumnDef } from "mantine-react-table";

const DEFAULT_FALLBACK_LABEL = "N/A";

const isNumericSegment = (segment: string): boolean => /^\d+$/.test(segment.trim());

const normaliseFieldPath = (field: string): string => field.replace(/^metadata\./, "");

export const buildColumnId = (namespace: string, field: string): string => `${namespace}__${field}`;

export const buildAccessorKey = (field: string): string => {
    const normalised = normaliseFieldPath(field);
    return normalised ? `fields.${normalised}` : "fields";
};

type MetadataRowLike = {
    fields?: Record<string, unknown>;
    _source?: {
        metadata?: Record<string, unknown>;
        [key: string]: unknown;
    };
    [key: string]: unknown;
};

export const getMetadataValue = <T extends MetadataRowLike>(row: T, field: string): unknown => {
    const metadata =
        // biome-ignore lint/suspicious/noExplicitAny: dynamic metadata structure from API
        (row as any)?.fields ?? (row as any)?._source?.metadata ?? (row as any)?._source ?? {};

    const normalised = normaliseFieldPath(field);
    if (!normalised) return metadata;

    const segments = normalised.split(".");
    let current: unknown = metadata;

    for (let index = 0; index < segments.length; index++) {
        if (current === null || current === undefined) return undefined;

        const segment = segments[index];
        const isLastSegment = index === segments.length - 1;

        if (Array.isArray(current)) {
            if (isNumericSegment(segment)) {
                current = current[Number(segment)];
                continue;
            }

            const [firstItem] = current;
            if (firstItem === undefined || firstItem === null) return undefined;
            current = firstItem;
        }

        if (typeof current !== "object") return undefined;

        current = (current as Record<string, unknown>)[segment];

        // Flatten arrays of primitives only at the very end so string joins can handle them later
        if (Array.isArray(current) && !isLastSegment) {
            const [firstItem] = current;
            current = firstItem;
        }
    }

    return current ?? undefined;
};

export const parseDate = (value: unknown): Date | undefined => {
    if (!value) return undefined;

    // Handle Unix timestamps: if it's a number with ~10 digits, it's in seconds and needs conversion to milliseconds
    let timestamp = value;
    if (typeof value === "number" && value < 10000000000) {
        timestamp = value * 1000; // Convert seconds to milliseconds
    }

    const date =
        timestamp instanceof Date ? new Date(timestamp) : new Date(timestamp as string | number);
    if (Number.isNaN(date.getTime())) return undefined;
    date.setHours(0, 0, 0, 0);
    return date;
};

export const formatDateForCell = (
    value: Date | undefined,
    fallbackLabel = DEFAULT_FALLBACK_LABEL,
) => {
    if (!value) return fallbackLabel;
    return value.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
};

type ColumnOverrides<T extends MetadataRowLike> = Partial<MRT_ColumnDef<T>>;

interface BaseColumnOptions<T extends MetadataRowLike> {
    namespace: string;
    field: string;
    header: string;
    fallback?: string;
    overrides?: ColumnOverrides<T>;
    accessor?: (row: T) => unknown;
}

export const createStringColumn = <T extends MetadataRowLike>(
    options: BaseColumnOptions<T>,
): MRT_ColumnDef<T> => {
    const {
        namespace,
        field,
        header,
        fallback = DEFAULT_FALLBACK_LABEL,
        accessor,
        overrides,
    } = options;

    return {
        id: buildColumnId(namespace, field),
        header,
        accessorKey: buildAccessorKey(field),
        accessorFn: (row) => {
            const value = accessor ? accessor(row) : getMetadataValue(row, field);
            if (value === undefined || value === null || value === "") {
                return fallback;
            }
            return value;
        },
        filterFn: "includesString",
        ...overrides,
    } satisfies MRT_ColumnDef<T>;
};

interface DateColumnOptions<T extends MetadataRowLike>
    extends Omit<BaseColumnOptions<T>, "fallback"> {
    fallbackLabel?: string;
}

export const createDateColumn = <T extends MetadataRowLike>(
    options: DateColumnOptions<T>,
): MRT_ColumnDef<T> => {
    const {
        namespace,
        field,
        header,
        accessor,
        overrides,
        fallbackLabel = DEFAULT_FALLBACK_LABEL,
    } = options;

    return {
        id: buildColumnId(namespace, field),
        header,
        accessorKey: buildAccessorKey(field),
        accessorFn: (row) => {
            const rawValue = accessor ? accessor(row) : getMetadataValue(row, field);
            return parseDate(rawValue);
        },
        Cell: ({ cell }) => formatDateForCell(cell.getValue<Date | undefined>(), fallbackLabel),
        filterVariant: "date-range",
        sortingFn: "datetime",
        ...overrides,
    } satisfies MRT_ColumnDef<T>;
};

export const normalizeStringArray = (
    value?: string[] | string,
    fallback = DEFAULT_FALLBACK_LABEL,
): string => {
    if (Array.isArray(value)) {
        const joined = value.join(", ");
        return joined || fallback;
    }
    if (typeof value === "string") return value || fallback;
    return fallback;
};
